import { effect, inject, Injectable, Injector, Signal, signal, WritableSignal } from '@angular/core';
import { DASHBOARD_CONFIG } from '../dashboard-grid.tokens';
import { DashboardRefreshSettings } from '../layout/dashboard-layout';
import { DashboardStateField, filterDefaultOf } from './dashboard-state';

/**
 * The part of the dashboard state a widget uses, e.g. `injectDashboardState<{ selected: WritableSignal<string> }>()`.
 * `provideDashboard` checks at compile time that the dashboard's state has it.
 */
export function injectDashboardState<Needs extends object>(): Needs {
  const context = inject(DashboardContext);
  return context.state as Needs;
}

/** Increments on each dashboard refresh; `dashboardWidgetResource` reloads on it for you */
export function injectDashboardRefresh(): Signal<number> {
  const context = inject(DashboardContext);
  return context.refreshTick;
}

/** Provided by `<app-dashboard-grid>`; widgets reach it through `injectDashboardState`. */
@Injectable()
export class DashboardContext {
  private readonly config = inject(DASHBOARD_CONFIG);
  private readonly fields = this.config.state;
  private readonly injector = inject(Injector);
  private readonly signals = new Map<string, WritableSignal<unknown>>(
    this.fields.map((field) => [field.name, signal<unknown>(field.defaultValue)]),
  );
  private defaults: Record<string, unknown> = {};

  private readonly refreshCount = signal(0);
  private readonly lastUpdatedAt = signal(new Date());

  readonly state: Readonly<Record<string, WritableSignal<unknown>>> = Object.freeze(Object.fromEntries(this.signals));
  readonly refreshTick: Signal<number> = this.refreshCount.asReadonly();
  readonly lastUpdated: Signal<Date> = this.lastUpdatedAt.asReadonly();

  /** Filter fields in the order a layout lists them; unknown names and non-filters are skipped */
  filterFields(names: readonly string[]): DashboardStateField[] {
    return names
      .map((name) => this.fields.find((field) => field.name === name))
      .filter((field): field is DashboardStateField => !!field?.filter);
  }

  valueOf(name: string): Signal<unknown> {
    return this.signalOf(name).asReadonly();
  }

  setValue(name: string, value: unknown): void {
    this.signalOf(name).set(value);
  }

  /** Applies a layout's filter defaults and resets every entry to its default */
  applyDefaults(filterDefaults: Record<string, unknown> = {}): void {
    this.defaults = filterDefaults;
    this.resetFilters();
  }

  resetFilters(): void {
    for (const field of this.fields) {
      this.signals.get(field.name)?.set(filterDefaultOf(field, this.defaults));
    }
  }

  refresh(): void {
    this.refreshCount.update((count) => count + 1);
    this.lastUpdatedAt.set(new Date());
  }

  /** Refreshes on the interval while enabled, until the dashboard is destroyed */
  autoRefresh(settings: Signal<DashboardRefreshSettings | undefined>): void {
    effect(
      (onCleanup) => {
        const current = settings();
        if (current?.enabled) {
          const timer = setInterval(() => this.refresh(), current.intervalSeconds * 1000);
          onCleanup(() => clearInterval(timer));
        }
      },
      { injector: this.injector },
    );
  }

  private signalOf(name: string): WritableSignal<unknown> {
    const found = this.signals.get(name);
    if (!found) {
      throw new Error(`Dashboard state has no "${name}"; add it to provideDashboard({ state })`);
    }

    return found;
  }
}
